import { i18n } from "@tellescope/i18n";
import type { IntegrationName } from "@tellescope/tanstack-query";
import { cn } from "@tellescope/ui";
import tellescopeLogoUrl from "../../assets/tellescope-transparent-flat-logo.png?w=96&format=webp&imagetools";
import { IntegrationOrbitItem } from "./IntegrationOrbitItem";
import { getAllIntegrationsMap } from "../../constants";

/** Fixed number of evenly distributed radial slots, filled clockwise starting at noon */
const ORBIT_SLOT_COUNT = 14;

export interface IntegrationOrbitProps {
  selectedIntegrations: IntegrationName[];
  highlightedIntegrations?: IntegrationName[];
  completedIntegrations?: IntegrationName[];
}

export const IntegrationOrbit = ({
  selectedIntegrations,
  highlightedIntegrations = [],
  completedIntegrations = [],
}: IntegrationOrbitProps) => {
  const allIntegrationsMap = getAllIntegrationsMap();

  return (
    <div className="relative flex items-center justify-center w-full">
      <div
        style={{
          backgroundImage:
            "linear-gradient(rgba(156, 163, 175, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(156, 163, 175, 0.3) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          maskImage: "radial-gradient(circle, black 60%, transparent 100%)",
        }}
        className="opacity-30 absolute top-0 bottom-0 left-0 right-0"
      />
      <div className="relative flex items-center justify-center [--orbit-radius:100px] md:[--orbit-radius:130px]">
        {selectedIntegrations.map((name, index) => {
          const isHighlighted = highlightedIntegrations.includes(name);
          const isCompleted = completedIntegrations.includes(name);

          return (
            <IntegrationOrbitItem
              key={name}
              integration={allIntegrationsMap[name]}
              index={index}
              angleDegrees={(index * 360) / ORBIT_SLOT_COUNT}
              isAnimated
              isHighlighted={isHighlighted}
              isCompleted={isCompleted}
              isDisabled={isCompleted || (!!highlightedIntegrations.length && !isHighlighted)}
            />
          );
        })}
        <div className="relative z-20 flex">
          {!!selectedIntegrations.length && !highlightedIntegrations.length && (
            <div className="animate-ping absolute bottom-0 left-0 right-0 top-0 rounded-xl bg-gray-200" />
          )}
          <div
            className={cn(
              "relative rounded-xl p-2 transition-all",
              selectedIntegrations.length ? "bg-white shadow-lg" : "bg-transparent"
            )}
          >
            <img src={tellescopeLogoUrl} alt={i18n.t("Tellescope logo", { ns: "onboarding" })} className="h-12 w-12" />
          </div>
        </div>
      </div>
    </div>
  );
};
